<template>
  <div class="image-upload">
    <!-- 单图模式 -->
    <el-upload
      v-if="!multiple"
      class="avatar-uploader"
      :class="{ 'is-circle': circle }"
      :show-file-list="false"
      :http-request="customUpload"
      :before-upload="beforeUpload"
      :on-success="handleSingleSuccess"
      :on-error="handleError"
    >
      <img v-if="modelValue" :src="modelValue" class="avatar" />
      <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
    </el-upload>

    <!-- 多图模式 -->
    <el-upload
      v-else
      list-type="picture-card"
      :file-list="fileList"
      :http-request="customUpload"
      :before-upload="beforeUpload"
      :on-success="handleMultiSuccess"
      :on-remove="handleRemove"
      :on-error="handleError"
      :on-preview="handlePreview"
      :before-remove="beforeRemove"
    >
      <el-icon><Plus /></el-icon>
    </el-upload>

    <!-- 放大预览：picture-card 的放大镜只 emit preview 事件，这里用 image-viewer 承接 -->
    <el-image-viewer
      v-if="previewVisible"
      :url-list="previewList"
      :initial-index="previewIndex"
      hide-on-click-modal
      teleported
      @close="previewVisible = false"
    />

    <div v-if="tip" class="upload-tip">{{ tip }}</div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import request from '../api/request'
import { trackUpload } from '../utils/pendingUploads'

const props = defineProps({
  modelValue: { type: [String, Array], default: '' },
  multiple: { type: Boolean, default: false },
  // 上传子目录：products / banners / categories / avatars / common
  // 避免所有图片混在 uploads 根目录
  dir: { type: String, default: 'common' },
  // 压缩参数可覆盖：轮播图等宽幅图用更大的最长边
  maxEdge: { type: Number, default: 800 },
  targetKb: { type: Number, default: 200 },
  // 圆形预览：头像类图片用，预览效果与顶栏/列表里的圆形头像一致
  circle: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue'])

// 压缩参数：最长边 800px、JPEG 质量 0.82、目标 <=200KB（可由 props 覆盖）
const COMPRESS = computed(() => ({
  maxEdge: props.maxEdge,
  quality: 0.82,
  targetKB: props.targetKb
}))

const tip = computed(() => {
  const edge = props.maxEdge
  return props.multiple
    ? `支持多张，上传时自动压缩至最长边 ${edge}px`
    : `上传时自动压缩至最长边 ${edge}px`
})

// 多图：把字符串数组同步成 el-upload 需要的 fileList
const fileList = ref([])
watch(
  () => props.modelValue,
  (val) => {
    if (props.multiple && Array.isArray(val)) {
      fileList.value = val.map((url, i) => ({ uid: i, url, name: url }))
    }
  },
  { immediate: true }
)

function beforeUpload(file) {
  // 不限制文件大小：上传时会自动压缩后再落盘
  if (!file.type.startsWith('image/')) {
    ElMessage.error('只能上传图片文件')
    return false
  }
  return true
}

/** 把图片绘制到 canvas 并导出压缩后的 JPEG */
async function renderToBlob(img, name) {
  let scale = Math.min(1, COMPRESS.value.maxEdge / Math.max(img.width, img.height))
  let quality = COMPRESS.value.quality

  for (let i = 0; i < 6; i++) {
    const w = Math.max(1, Math.round(img.width * scale))
    const h = Math.max(1, Math.round(img.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff' // PNG 透明区转 JPEG 时补白底
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, w, h)

    const out = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
    if (!out) throw new Error('图片压缩失败')

    if (out.size <= COMPRESS.value.targetKB * 1024) {
      return new File([out], baseName(name) + '.jpg', { type: 'image/jpeg' })
    }
    // 先降质量，质量到底后缩小尺寸
    if (quality > 0.5) quality = Math.max(0.5, quality - 0.1)
    else scale = scale * 0.8
  }
  throw new Error('图片压缩失败')
}

function baseName(name) {
  return (name || 'image').replace(/\.[^.]+$/, '')
}

/** 上传时压缩入口 */
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      renderToBlob(img, file.name).then(resolve).catch(reject)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('图片读取失败'))
    }
    img.src = url
  })
}

/** 自定义上传：压缩 -> 提交，返回结果交由 el-upload 处理 */
async function customUpload(options) {
  const { file, onProgress } = options
  onProgress && onProgress({ percent: 10 })

  const compressed = await compressImage(file)
  const fd = new FormData()
  // 先写字段再写文件，确保后端 multer 能解析到压缩参数
  fd.append('dir', props.dir)
  fd.append('maxEdge', String(props.maxEdge))
  fd.append('targetBytes', String(props.targetKb * 1024))
  fd.append('file', compressed)
  onProgress && onProgress({ percent: 60 })

  const res = await request.post('/admin/upload', fd, { timeout: 60000 })
  onProgress && onProgress({ percent: 100 })

  const kb = (n) => (n / 1024).toFixed(0) + 'KB'
  if (compressed.size < file.size) {
    ElMessage.success(`已压缩 ${kb(file.size)} → ${kb(compressed.size)}`)
  }
  // 直接返回 {code,msg,data}：el-upload 会把该 Promise 的结果交给 on-success。
  // 不要手动调用 options.onSuccess —— 否则会被调用两次（第二次为 undefined）而误报“上传失败”
  return res
}

function handleSingleSuccess(res) {
  if (res && res.code === 0) {
    trackUpload(res.data.url)
    emit('update:modelValue', res.data.url)
  } else {
    ElMessage.error((res && res.msg) || '上传失败')
  }
}

function handleMultiSuccess(res) {
  if (res && res.code === 0) {
    trackUpload(res.data.url)
    const list = Array.isArray(props.modelValue) ? [...props.modelValue] : []
    list.push(res.data.url)
    emit('update:modelValue', list)
  } else {
    ElMessage.error((res && res.msg) || '上传失败')
  }
}

function handleRemove(file) {
  const list = (Array.isArray(props.modelValue) ? props.modelValue : []).filter(
    (url) => url !== file.url
  )
  emit('update:modelValue', list)
}

// 删除前二次确认：返回 false / reject 会阻止 el-upload 移除该项
async function beforeRemove() {
  try {
    await ElMessageBox.confirm('确认删除这张图片？', '提示', {
      type: 'warning',
      confirmButtonText: '删除',
      cancelButtonText: '取消'
    })
    return true
  } catch {
    return false
  }
}

// 放大镜预览：picture-card 的预览图标只 emit preview，需要自己挂查看器
const previewVisible = ref(false)
const previewIndex = ref(0)
const previewList = computed(() =>
  Array.isArray(props.modelValue) ? props.modelValue.filter(Boolean) : []
)
function handlePreview(file) {
  const list = previewList.value
  const idx = list.findIndex((url) => url === (file && file.url))
  previewIndex.value = idx > -1 ? idx : 0
  previewVisible.value = list.length > 0
}

function handleError() {
  ElMessage.error('上传失败，请检查网络或登录状态')
}
</script>

<style scoped>
.avatar-uploader :deep(.el-upload) {
  border: 1px dashed #d9d9d9;
  border-radius: 0;
  cursor: pointer;
  position: relative;
  overflow: visible;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
}
.avatar-uploader :deep(.el-upload):hover {
  border-color: #409eff;
}
.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}
.avatar-uploader :deep(.el-upload img) {
  width: 120px;
  height: 120px;
  display: block;
  object-fit: contain;
}
.avatar {
  width: 120px;
  height: 120px;
  display: block;
  object-fit: contain;
}
/* 圆形预览：头像类图片，效果与顶栏/列表的圆形头像一致（居中裁剪填充） */
.avatar-uploader.is-circle :deep(.el-upload) {
  border-radius: 50%;
  overflow: hidden;
}
.avatar-uploader.is-circle .avatar,
.avatar-uploader.is-circle :deep(.el-upload img) {
  border-radius: 50%;
  object-fit: cover;
}
/* 多图 picture-card 缩略图同样完整显示，不裁剪 */
.image-upload :deep(.el-upload-list--picture-card .el-upload-list__item-thumbnail) {
  object-fit: contain;
}
.upload-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
</style>
